import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import Header from '../../components/common/Header';
import BottomNav from '../../components/common/BottomNav';

// ── Decode QR from an image File ────────────────────────────────────────────
function decodeQRFromFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width  = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(data.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' });
      if (code?.data) resolve(code.data);
      else reject(new Error('No QR code found in image. Make sure the image is clear and not cropped.'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image file.')); };
    img.src = url;
  });
}

export default function QRScannerPage() {
  const navigate     = useNavigate();
  const videoRef     = useRef(null);
  const canvasRef    = useRef(null);
  const streamRef    = useRef(null);
  const rafRef       = useRef(null);
  const activeRef    = useRef(true);
  const lastScanRef  = useRef('');
  const fileInputRef = useRef(null);

  const [status, setStatus]         = useState('loading');
  const [errMsg, setErrMsg]         = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const [scanPct, setScanPct]       = useState(0);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const handleScan = useCallback((raw) => {
    if (!raw) return;
    const trimmed = raw.trim();
    if (trimmed === lastScanRef.current) return;
    lastScanRef.current = trimmed;
    stopCamera();
    setStatus('found');

    // Try URL like https://domain.com/doctor/:id
    try {
      const url   = new URL(trimmed);
      const parts = url.pathname.split('/');
      const idx   = parts.findIndex(p => p === 'doctor');
      if (idx >= 0 && parts[idx + 1]) {
        setTimeout(() => navigate(`/doctor/${parts[idx + 1]}`), 700);
        return;
      }
    } catch {}

    // Raw 24-char hex ObjectId
    if (/^[0-9a-f]{24}$/i.test(trimmed)) {
      setTimeout(() => navigate(`/doctor/${trimmed}`), 700);
      return;
    }

    setStatus('error');
    setErrMsg(`Not a FARISTAA doctor QR. Scanned: ${trimmed.slice(0, 60)}`);
    lastScanRef.current = '';
  }, [navigate, stopCamera]);

  // Animated scan line
  useEffect(() => {
    if (status !== 'scanning') return;
    let pct = 0, dir = 1;
    const id = setInterval(() => {
      pct += dir * 2;
      if (pct >= 100) { pct = 100; dir = -1; }
      if (pct <= 0)   { pct = 0;   dir =  1; }
      setScanPct(pct);
    }, 16);
    return () => clearInterval(id);
  }, [status]);

  // Scan loop — jsQR imported directly, no CDN
  const startScanLoop = useCallback(() => {
    const tick = () => {
      if (!activeRef.current) return;
      const video  = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(tick); return;
      }
      canvas.width  = video.videoWidth  || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Try BarcodeDetector first (fast native API)
      if (window.BarcodeDetector) {
        new window.BarcodeDetector({ formats: ['qr_code'] })
          .detect(canvas)
          .then(codes => {
            if (!activeRef.current) return;
            if (codes.length > 0) handleScan(codes[0].rawValue);
            else rafRef.current = requestAnimationFrame(tick);
          })
          .catch(() => {
            // Fallback to jsQR npm package
            const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' });
            if (code?.data) handleScan(code.data);
            else rafRef.current = requestAnimationFrame(tick);
          });
        return;
      }

      // jsQR only (Firefox, Safari, desktop Chrome)
      const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: 'attemptBoth' });
      if (code?.data) handleScan(code.data);
      else rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [handleScan]);

  // Init camera
  useEffect(() => {
    activeRef.current = true;
    let cancelled = false;

    const init = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('error');
        setErrMsg('Camera not available. Use the file upload option below instead.');
        return;
      }

      let stream = null;
      for (const c of [
        { video: { facingMode: { exact: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } },
        { video: { facingMode: 'environment' } },
        { video: { facingMode: 'user' } },
        { video: true },
      ]) {
        try { stream = await navigator.mediaDevices.getUserMedia(c); break; }
        catch (err) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setStatus('error');
            setErrMsg('Camera permission denied. Allow camera access in browser settings, or use the file upload option below.');
            return;
          }
        }
      }

      if (cancelled) { stream?.getTracks().forEach(t => t.stop()); return; }

      if (!stream) {
        setStatus('error');
        setErrMsg('Could not open camera. Use the file upload option below.');
        return;
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        try { await videoRef.current.play(); } catch {}
      }
      if (!cancelled) { setStatus('scanning'); startScanLoop(); }
    };

    init();
    return () => { cancelled = true; activeRef.current = false; stopCamera(); };
  }, [startScanLoop, stopCamera]);

  // File upload — decode with jsQR npm package directly
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setFileLoading(true);
    try {
      const raw = await decodeQRFromFile(file);
      lastScanRef.current = '';
      handleScan(raw);
    } catch (err) {
      setStatus('error');
      setErrMsg(err.message);
      lastScanRef.current = '';
    } finally {
      setFileLoading(false);
    }
  };

  const retry = () => {
    setStatus('loading');
    setErrMsg('');
    lastScanRef.current = '';
    stopCamera();
    activeRef.current = true;
    // Re-init camera
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setStatus('error'); setErrMsg('Camera not available.'); return; }
      let stream = null;
      for (const c of [{ video: { facingMode: 'environment' } }, { video: true }]) {
        try { stream = await navigator.mediaDevices.getUserMedia(c); break; } catch {}
      }
      if (!stream) { setStatus('error'); setErrMsg('Could not open camera. Use file upload.'); return; }
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.muted = true; videoRef.current.play().catch(() => {}); }
      setStatus('scanning');
      startScanLoop();
    })();
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Header title="Scan Doctor QR" showBack />

      <div className="flex-1 pt-14 pb-16 flex flex-col items-center justify-center">

        {/* Camera box */}
        <div className="relative w-full max-w-sm mx-auto" style={{ aspectRatio: '1/1' }}>
          <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover bg-black" />
          <canvas ref={canvasRef} className="hidden" />

          {/* Scanning overlay */}
          {status === 'scanning' && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(rgba(0,0,0,0.45) 0%, transparent 25%, transparent 75%, rgba(0,0,0,0.45) 100%)'
              }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-60 h-60">
                  {['top-0 left-0 border-t-[3px] border-l-[3px]',
                    'top-0 right-0 border-t-[3px] border-r-[3px]',
                    'bottom-0 left-0 border-b-[3px] border-l-[3px]',
                    'bottom-0 right-0 border-b-[3px] border-r-[3px]',
                  ].map((cls, i) => (
                    <div key={i} className={`absolute w-8 h-8 border-primary ${cls}`} style={{ borderRadius: 2 }} />
                  ))}
                  {/* Laser scan line */}
                  <div className="absolute left-0 right-0 h-[2px] bg-primary"
                    style={{ top: `${scanPct}%`, boxShadow: '0 0 8px 3px rgba(13,148,136,0.7)', transition: 'top 16ms linear' }} />
                </div>
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center">
                <span className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full font-medium">🔍 Scanning...</span>
              </div>
            </div>
          )}

          {/* Loading */}
          {status === 'loading' && (
            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3">
              <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <p className="text-white/80 text-sm">Starting camera...</p>
            </div>
          )}

          {/* Found */}
          {status === 'found' && (
            <div className="absolute inset-0 bg-green-900/60 flex items-center justify-center">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center"
                style={{ boxShadow: '0 0 40px rgba(16,185,129,0.6)' }}>
                <i className="fa-solid fa-check text-white text-4xl" />
              </div>
            </div>
          )}
        </div>

        {/* Status text */}
        <div className="px-5 py-4 text-center w-full max-w-sm mx-auto">
          {status === 'scanning' && (
            <div className="space-y-1">
              <p className="text-white font-bold text-base">Point camera at Doctor QR Code</p>
              <p className="text-white/60 text-sm">Scans automatically · opens doctor profile</p>
            </div>
          )}
          {status === 'found' && (
            <div className="space-y-1">
              <p className="text-white font-bold text-lg">QR Detected!</p>
              <p className="text-white/70 text-sm">Opening doctor profile...</p>
            </div>
          )}
          {status === 'error' && (
            <div className="space-y-3">
              <div className="bg-red-900/60 border border-red-500/50 rounded-2xl p-4 text-left">
                <div className="flex items-start gap-2">
                  <i className="fa-solid fa-circle-exclamation text-red-400 text-base flex-shrink-0 mt-0.5" />
                  <p className="text-red-200 text-sm leading-relaxed">{errMsg}</p>
                </div>
              </div>
              <button onClick={retry}
                className="w-full py-3 bg-primary text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2">
                <i className="fa-solid fa-rotate" /> Try Camera Again
              </button>
            </div>
          )}
        </div>

        {/* File upload + manual input */}
        <div className="w-full max-w-sm mx-auto px-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-white/40 text-xs font-semibold">OR</span>
            <div className="flex-1 h-px bg-white/20" />
          </div>

          {/* Upload QR image button */}
          <button onClick={() => fileInputRef.current?.click()} disabled={fileLoading}
            className="w-full flex items-center justify-center gap-2 py-3 bg-white/10 border border-white/25 text-white rounded-2xl text-sm font-semibold active:scale-95 transition-all disabled:opacity-50">
            {fileLoading
              ? <><i className="fa-solid fa-spinner animate-spin" /> Reading QR from image...</>
              : <><i className="fa-solid fa-image" /> Upload Doctor QR Image</>}
          </button>
          <p className="text-white/40 text-xs text-center">Select a QR code image from your gallery or files</p>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />

          {/* Manual paste */}
          <div className="bg-white/10 rounded-2xl p-3 space-y-2">
            <p className="text-white/60 text-xs font-semibold">Or paste doctor profile link:</p>
            <ManualInput onSubmit={handleScan} />
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function ManualInput({ onSubmit }) {
  const [val, setVal] = useState('');
  return (
    <div className="flex gap-2">
      <input value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && val && onSubmit(val)}
        placeholder="https://... or Doctor ID"
        className="flex-1 bg-white/10 text-white placeholder:text-white/30 px-3 py-2 rounded-xl text-sm outline-none border border-white/20 focus:border-primary/60" />
      <button onClick={() => val && onSubmit(val)}
        className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold flex-shrink-0">Go</button>
    </div>
  );
}
